/**
 * <anja-action-gallery>: lightbox for "See it in action" media plus play-when-visible for inline videos.
 * Native <dialog> provides focus containment and Escape; arrow keys step through items.
 */
class AnjaActionGallery extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #index = 0;
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    this.stage = this.querySelector('[data-anja-action-stage]');
    this.items = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.anja-action__item')));
    if (!this.dialog || !this.items.length) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const opener = target.closest('[data-anja-action-open]');
        if (opener instanceof HTMLElement) {
          this.#opener = opener;
          this.#show(Number(opener.dataset.anjaActionOpen) - 1);
          this.dialog?.showModal();
          return;
        }
        const step = target.closest('[data-anja-action-step]');
        if (step instanceof HTMLElement) this.#show(this.#index + Number(step.dataset.anjaActionStep));
        if (target.closest('[data-anja-action-close]') || target === this.dialog) this.dialog?.close();
      },
      { signal }
    );
    this.dialog.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') this.#show(this.#index + (rtl ? -1 : 1));
        if (event.key === 'ArrowLeft') this.#show(this.#index + (rtl ? 1 : -1));
      },
      { signal }
    );
    this.dialog.addEventListener(
      'close',
      () => {
        if (this.stage) this.stage.innerHTML = '';
        this.#opener?.focus();
      },
      { signal }
    );

    // Inline videos only play while on screen, and never for reduced-motion visitors.
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        }
      });
      this.querySelectorAll('.anja-action__video').forEach((v) => this.#observer?.observe(v));
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  /** @param {number} index */
  #show(index) {
    const count = this.items.length;
    this.#index = ((index % count) + count) % count;
    const template = this.items[this.#index].querySelector('template[data-anja-action-full]');
    if (this.stage && template instanceof HTMLTemplateElement) {
      this.stage.replaceChildren(template.content.cloneNode(true));
    }
  }
}

if (!customElements.get('anja-action-gallery')) customElements.define('anja-action-gallery', AnjaActionGallery);
